import * as React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer, Footer, GovernmentIdentity, IconButton, NotificationBell, useLocale, useTheme, useToast, type NotificationItem } from '@ministry/ui/internal';
// `@ministry/ui` ships no raw icon set on its public API surface (StatusBadge etc. wire
// their own icons internally) — the app imports its own chrome icons the same way
// packages/ui does: one module per icon, never the `lucide-react` barrel.
import Sun from 'lucide-react/dist/esm/icons/sun.js';
import Moon from 'lucide-react/dist/esm/icons/moon.js';
import Menu from 'lucide-react/dist/esm/icons/menu.js';

import { logout } from '../lib/auth.js';
import { SESSION_QUERY_KEY, useSession } from '../lib/session.js';
import { clearHadSession } from '../lib/had-session.js';
import { ROUTES, requiresExactMatch, visibleNavItems, type NavItem } from '../lib/nav.js';
import { visibleAdminNavItems } from '../routes/admin/index.js';
import { roleLabel } from '../lib/roles.js';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  unreadNotificationCount,
} from '../lib/notifications.js';
import { formatInstantDisplay } from '../lib/dates.js';
import { getBuildId } from '../lib/build-info.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from './LocaleToggle.js';
import { AccountMenu } from './AccountMenu.js';

/**
 * DESIGN_SYSTEM.md §5: "fixed-width collapsible side navigation + top bar (search,
 * notification bell, account menu) + content area". `lib/nav.ts`'s `visibleNavItems`
 * gates each item on `permissions` from `/auth/me` (never `roleCode` — CLAUDE.md's brief:
 * re-deriving from a role name drifts), matching this file's own earlier comment about
 * why the nav is not built against invented roles.
 *
 * ux-review defects fixed in this pass (see UX_REVIEW.md):
 *  - The language and theme controls were full text buttons ("भाषा बदल्नुहोस्: NE",
 *    "रङ थिम बदल्नुहोस्: उज्यालो") dominating the bar — now compact icon controls with
 *    a full accessible name (`LocaleToggle`, and the theme `IconButton` below).
 *  - The notification bell was a permanently disabled dead control with no visible
 *    (sighted-user) explanation, only a `title` attribute — removed until I11 (the
 *    Notification Centre) actually exists, rather than training staff to ignore it.
 *  - The user's name plus a bare "Log out" link next to it is now a single
 *    `AccountMenu` (name + role, opening to Account Settings and Log out) — see that
 *    component's own comment for why logout moved behind a menu.
 *  - The wordmark was hardcoded English ("Ministry Portal") even in the Nepali locale,
 *    and carried no emblem/mark of any kind. It is now the real government lockup —
 *    `GovernmentIdentity` (`@ministry/ui`) — which localizes with the locale, renders
 *    in the dedicated government-identity color (never the ministry's own brand blue,
 *    never a reuse of `status-critical`), and reserves the emblem slot without drawing
 *    or approximating the actual Emblem of Nepal (A-019 — see that component's comment).
 *    The ministry-name line is intentionally omitted here: `/auth/me` does not
 *    currently return the tenant's `nameNe`/`nameEn` (see
 *    `docs/01-architecture/VERIFIED_RESPONSES.md`), so this renders "Government of
 *    Nepal" alone rather than inventing a ministry name — a complete, correct lockup
 *    on its own, per the reference prototype's own masthead pattern.
 *  - Design pass (this round): the full "नेपाल सरकार" wordmark next to the emblem, repeated
 *    on every single internal page's top bar, was named directly as noise — a citizen sees
 *    it once (the public header, kept in full); a staff member sees it hundreds of times a
 *    day. `GovernmentIdentity` now renders `variant="mark-only"` here — emblem alone, still
 *    with its own real, localized `alt` text (see that component's comment) — while the
 *    FULL lockup is kept exactly where it still does work: the login screen
 *    (`LoginPage.tsx`) and the citizen portal's own header, neither of which is this file.
 *  - The account menu's role text was `roleCode` verbatim ("section_officer") — a raw
 *    permission-system identifier, not a role name — read through `roleLabel` now, same
 *    fix applied to the Dashboard subtitle and Account Settings.
 */
export function AppShell(): React.JSX.Element {
  const { theme, setPreference } = useTheme();
  const { locale } = useLocale();
  const t = useT();
  const { push } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useSession();
  const items = visibleNavItems(data?.permissions ?? []);
  // The administration surface is a SEPARATE nav group, not four more rows appended to
  // the operational list. A Section Officer sees no group and no heading at all —
  // `visibleAdminNavItems` gates on the same permission strings the server's own routes
  // require, so an empty result means the heading is never rendered rather than rendered
  // over nothing.
  const adminItems = visibleAdminNavItems(data?.permissions ?? []);
  const canSeeNotifications = data?.permissions.includes('notification.read') ?? false;
  /**
   * ux-review defect closed in this pass: the sidebar `<nav>` below is `hidden md:block`
   * (§5.1's responsive rule — a fixed-width rail makes no sense under ~768px), and until
   * now NOTHING replaced it below that width. Every nav item — not just the pre-existing
   * three — was completely unreachable on a phone-width screen with no hamburger, no
   * drawer, nothing; only a direct URL got a mobile user anywhere past the dashboard.
   * `Drawer` (`@ministry/ui/internal`, already used nowhere in this app before now) is the
   * exact component `DESIGN_SYSTEM.md` names for a side-anchored panel — this is the same
   * nav list, not a second, divergent one.
   */
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
      // A deliberate logout is not a lapsed session — the next visit to a protected route
      // should get the plain login screen, not `SessionExpiredPage`'s "your session
      // expired" copy (that copy is reserved for a session that stopped working without
      // the user asking it to).
      clearHadSession();
      navigate('/login', { replace: true });
    },
  });

  /**
   * I11 (Notification Centre) — the bell the design pass removed as "a permanently dead
   * control" (see this file's own comment above) because nothing was wired to it. All four
   * endpoints exist now (API_CONTRACT.md §13.7).
   *
   * §13.7: "On failure the client keeps the LAST KNOWN count rather than showing zero" — a
   * false "all read" would be actively harmful in a system whose whole purpose is "don't
   * let a file silently stall" (SCREEN_SPECS.md I11). `placeholderData: keepPreviousData`
   * is exactly that rule: a refetch that fails leaves the last successful count on screen
   * instead of the query's own `isError` blanking it to undefined.
   */
  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: ({ signal }) => unreadNotificationCount(signal),
    enabled: canSeeNotifications,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });

  const feedQuery = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: ({ signal }) => listNotifications({ limit: 20 }, signal),
    enabled: canSeeNotifications,
  });

  const invalidateNotifications = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      invalidateNotifications();
      // Consistency with `NotificationCentrePage`'s own "mark all read" action, which
      // gets the same toast — one mechanism for the same action, wherever it's
      // triggered from (the bell's quick panel here, or the full page there).
      push({ variant: 'success', title: t('toast.notifications.markedAll') });
    },
  });

  const notificationItems: NotificationItem[] = (feedQuery.data?.items ?? []).map((row) => ({
    id: row.id,
    title: row.body,
    when: formatInstantDisplay(row.createdAt, locale),
    read: row.readAt !== null,
  }));

  return (
    /*
      `h-screen` + `overflow-hidden`, not `min-h-screen`: this is an application frame,
      not a document. The chrome (header, side nav) stays put and ONLY the content
      column scrolls — so an administrator paging through a long user directory keeps
      the nav and the ministry identity on screen instead of scrolling them away and
      having to come back up to move anywhere. `min-h-0` on the row below is what makes
      that work: without it a flex child refuses to shrink below its content and the
      scrollbar lands on the page instead of the panel.
    */
    <div className="flex h-screen flex-col overflow-hidden bg-surface-page text-text-primary">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-surface-elevated focus:px-3 focus:py-2 focus:shadow-elevation-2"
      >
        {t('shell.skipToContent')}
      </a>

      <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border-decorative bg-surface-elevated px-4">
        <div className="flex items-center gap-2">
          <IconButton
            className="md:hidden"
            label={t('shell.nav.openMenu')}
            icon={<Menu width={20} height={20} />}
            onClick={() => setMobileNavOpen(true)}
          />
          <GovernmentIdentity size="sm" variant="mark-only" />
        </div>
        <div className="flex items-center gap-1">
          <LocaleToggle />
          {canSeeNotifications ? (
            <NotificationBell
              items={notificationItems}
              loading={feedQuery.isLoading}
              error={feedQuery.isError}
              onRetry={() => feedQuery.refetch()}
              unreadCount={unreadQuery.data?.unread}
              onItemClick={(item) => {
                if (!item.read) markReadMutation.mutate(item.id);
                const row = feedQuery.data?.items.find((r) => r.id === item.id);
                if (row?.subjectType === 'file' && row.subjectId) {
                  navigate(ROUTES.fileDetail(row.subjectId));
                }
              }}
              footer={
                notificationItems.some((item) => !item.read) ? (
                  <button
                    type="button"
                    onClick={() => markAllReadMutation.mutate()}
                    disabled={markAllReadMutation.isPending}
                    className="text-sm font-semibold text-text-link underline disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {markAllReadMutation.isPending ? t('notifications.markingAll') : t('notifications.markAllRead')}
                  </button>
                ) : undefined
              }
            />
          ) : null}
          <IconButton
            label={theme === 'dark' ? t('shell.theme.switchToLight') : t('shell.theme.switchToDark')}
            icon={theme === 'dark' ? <Moon width={18} height={18} /> : <Sun width={18} height={18} />}
            onClick={() => setPreference(theme === 'dark' ? 'light' : 'dark')}
          />
          {data ? (
            <AccountMenu
              name={data.user.fullName}
              role={roleLabel(data.roles[0]?.roleCode, locale)}
              loggingOut={logoutMutation.isPending}
              onLogout={() => logoutMutation.mutate()}
            />
          ) : null}
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/*
          The nav scrolls on its own axis. It has its own `overflow-y-auto` because a
          Super Admin's menu (operational items plus the Administration group) can be
          taller than a laptop viewport — without it the bottom entries become
          unreachable once the page itself stops scrolling.
        */}
        <nav
          aria-label={t('shell.nav.label')}
          className="hidden w-48 shrink-0 overflow-y-auto border-r border-border-decorative p-4 md:block"
        >
          <NavList items={items} t={t} />
          {adminItems.length > 0 ? (
            <NavList items={adminItems} t={t} headingKey="shell.nav.administration" />
          ) : null}
        </nav>
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          <main id="main-content" className="min-w-0 flex-1 p-6">
            <Outlet />
          </main>
          {/*
            The footer moved INSIDE the scrolling column. Pinned to the viewport it
            would spend a permanent strip of vertical space on a copyright line, on
            every screen, forever — and this shell no longer has a page scroll to push
            it off. It still appears exactly once, at the end of the content.
          */}
          <Footer systemName={t('shell.systemName')} buildId={getBuildId()} />
        </div>
      </div>


      {/* Same nav list, same permission filter — the drawer is a viewport-width
          alternative presentation of `items`, never a second copy that could drift. */}
      <Drawer open={mobileNavOpen} onOpenChange={setMobileNavOpen} title={t('shell.nav.label')} side="left">
        <NavList items={items} t={t} onNavigate={() => setMobileNavOpen(false)} />
        {adminItems.length > 0 ? (
          <NavList
            items={adminItems}
            t={t}
            headingKey="shell.nav.administration"
            onNavigate={() => setMobileNavOpen(false)}
          />
        ) : null}
      </Drawer>
    </div>
  );
}

function NavList({
  items,
  t,
  headingKey,
  onNavigate,
}: {
  items: readonly NavItem[];
  t: (key: string) => string;
  /** Renders a labelled group. Omitted = the primary, unlabelled list. */
  headingKey?: string;
  onNavigate?: () => void;
}): React.JSX.Element {
  return (
    <>
      {headingKey === undefined ? null : (
        <h2
          id={`nav-group-${headingKey}`}
          className="mt-6 mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-text-secondary"
        >
          {t(headingKey)}
        </h2>
      )}
      <ul
        className="flex flex-col gap-1"
        aria-labelledby={headingKey === undefined ? undefined : `nav-group-${headingKey}`}
      >
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            // See `requiresExactMatch` — both failure modes are documented there.
            end={requiresExactMatch(item.to, items)}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:shadow-focus ${
                isActive ? 'bg-surface-selected text-text-heading' : 'text-text-primary hover:bg-surface-hover'
              }`
            }
          >
            <item.icon aria-hidden="true" width={18} height={18} />
            {t(item.labelKey)}
          </NavLink>
        </li>
      ))}
      </ul>
    </>
  );
}
